import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import apiClient, { formatApiError } from "../api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import MediaPicker from "../components/MediaPicker";
import { ChevronLeft, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body">
      {label}
    </span>
    {children}
    {hint && <span className="text-[11px] text-zinc-600 font-body">{hint}</span>}
  </label>
);

const PageEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/admin/pages/${id}`)
      .then(({ data }) => {
        setPage(data);
        setForm(data);
      })
      .catch((e) => {
        toast.error(formatApiError(e));
        navigate("/admin/pages");
      });
  }, [id, navigate]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug,
        hero_title: form.hero_title,
        hero_subtitle: form.hero_subtitle,
        hero_image_id: form.hero_image_id,
        intro_text: form.intro_text,
        body_text: form.body_text,
        cta_text: form.cta_text,
        cta_link: form.cta_link,
        seo_title: form.seo_title,
        seo_description: form.seo_description,
        status: form.status,
      };
      const { data } = await apiClient.patch(`/admin/pages/${id}`, payload);
      setPage(data);
      setForm(data);
      toast.success("Pagina opgeslagen");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!page) {
    return <div className="text-sm text-zinc-500">Laden...</div>;
  }

  return (
    <div data-testid="admin-page-editor" className="pb-20">
      <Link
        to="/admin/pages"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-zinc-500 hover:text-champagne transition-colors font-body mb-8"
        data-testid="page-editor-back"
      >
        <ChevronLeft size={13} /> Terug naar pagina's
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
            Pagina · {page.key}
          </p>
          <h1 className="font-display text-5xl text-white tracking-tight">
            {form.title || page.title}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`/${page.slug === "home" ? "" : page.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-3 text-[11px] tracking-[0.3em] uppercase border border-white/10 text-zinc-300 hover:border-champagne hover:text-champagne transition-colors font-body"
          >
            <ExternalLink size={12} /> Bekijk live
          </a>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            data-testid="page-editor-save"
            className="inline-flex items-center gap-2 px-6 py-3 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold disabled:opacity-60"
          >
            <Save size={12} /> {saving ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 flex flex-col gap-6">
          <Section title="Algemeen">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Interne naam (titel)">
                <Input
                  data-testid="field-title"
                  value={form.title || ""}
                  onChange={(e) => onChange("title", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Slug" hint="URL-vriendelijke identifier (a-z, 0-9, -)">
                <Input
                  data-testid="field-slug"
                  value={form.slug || ""}
                  onChange={(e) => onChange("slug", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Status">
                <Select
                  value={form.status || "active"}
                  onValueChange={(v) => onChange("status", v)}
                >
                  <SelectTrigger
                    data-testid="field-status"
                    className="h-11 bg-ink-950 border-white/10 focus:border-champagne text-sm font-body text-white rounded-none"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ink-900 border-white/10 rounded-none">
                    <SelectItem value="active" className="text-sm font-body rounded-none">
                      Actief
                    </SelectItem>
                    <SelectItem value="hidden" className="text-sm font-body rounded-none">
                      Verborgen
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <Section title="Hero">
            <div className="grid grid-cols-1 gap-5">
              <Field label="Hero titel">
                <Input
                  data-testid="field-hero-title"
                  value={form.hero_title || ""}
                  onChange={(e) => onChange("hero_title", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="Hero subtitel">
                <Textarea
                  data-testid="field-hero-subtitle"
                  value={form.hero_subtitle || ""}
                  onChange={(e) => onChange("hero_subtitle", e.target.value)}
                  rows={2}
                  className="bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none resize-none"
                />
              </Field>
              <MediaPicker
                label="Hero afbeelding"
                value={form.hero_image_id}
                onChange={(id) => onChange("hero_image_id", id)}
              />
            </div>
          </Section>

          <Section title="Inhoud">
            <div className="grid grid-cols-1 gap-5">
              <Field label="Intro tekst">
                <Textarea
                  data-testid="field-intro-text"
                  value={form.intro_text || ""}
                  onChange={(e) => onChange("intro_text", e.target.value)}
                  rows={4}
                  className="bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none resize-none"
                />
              </Field>
              <Field label="Hoofdtekst (body)" hint="Ondersteunt eenvoudige opmaak met regelafstand.">
                <Textarea
                  data-testid="field-body-text"
                  value={form.body_text || ""}
                  onChange={(e) => onChange("body_text", e.target.value)}
                  rows={8}
                  className="bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none resize-none"
                />
              </Field>
            </div>
          </Section>

          <Section title="Call-to-action">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="CTA tekst">
                <Input
                  data-testid="field-cta-text"
                  value={form.cta_text || ""}
                  onChange={(e) => onChange("cta_text", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="CTA link" hint="Interne paden zoals /aanbod of externe URL.">
                <Input
                  data-testid="field-cta-link"
                  value={form.cta_link || ""}
                  onChange={(e) => onChange("cta_link", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
            </div>
          </Section>

          <Section title="SEO">
            <div className="grid grid-cols-1 gap-5">
              <Field label="SEO title" hint="Verschijnt in de tab van de browser & Google-resultaten.">
                <Input
                  data-testid="field-seo-title"
                  value={form.seo_title || ""}
                  onChange={(e) => onChange("seo_title", e.target.value)}
                  className="h-11 bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none"
                />
              </Field>
              <Field label="SEO description" hint="Korte omschrijving (150–160 tekens) voor zoekmachines.">
                <Textarea
                  data-testid="field-seo-description"
                  value={form.seo_description || ""}
                  onChange={(e) => onChange("seo_description", e.target.value)}
                  rows={3}
                  className="bg-ink-950 border-white/10 focus:border-champagne focus-visible:ring-0 text-sm font-body text-white rounded-none resize-none"
                />
              </Field>
            </div>
          </Section>
        </div>

        <aside className="lg:col-span-4 flex flex-col gap-6">
          <div className="bg-ink-900 border border-white/[0.06] p-6">
            <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-body mb-4">
              Pagina-info
            </p>
            <dl className="flex flex-col gap-3 text-xs font-body">
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500">Type</dt>
                <dd className="text-zinc-200">
                  {page.is_core_page ? "Kernpagina" : "Custom"}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500">Aangemaakt</dt>
                <dd className="text-zinc-200">
                  {new Date(page.created_at).toLocaleDateString("nl-NL")}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500">Laatst bewerkt</dt>
                <dd className="text-zinc-200">
                  {new Date(page.updated_at).toLocaleDateString("nl-NL")}
                </dd>
              </div>
            </dl>
          </div>

          <div className="bg-ink-900 border border-white/[0.06] p-6">
            <p className="text-[10px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
              Tip
            </p>
            <p className="text-sm text-zinc-300 font-body leading-relaxed">
              Contentblokken (zoals galerij, citaat, services) beheert u op de
              pagina <Link to="/admin/blocks" className="text-champagne underline">Contentblokken</Link>.
              Per pagina kunt u daar blokken toevoegen, herschikken en verbergen.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="bg-ink-900 border border-white/[0.06] p-7">
    <h2 className="font-display text-2xl text-white mb-6 tracking-tight">
      {title}
    </h2>
    {children}
  </section>
);

export default PageEditorPage;
